import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';

// Inventory (admins only): physical meters in stock, who is responsible for them,
// what they cost and sell for, and invoices for meters sold.

const panel = { background: 'rgba(15,23,42,0.92)', borderRadius: 16, border: '1px solid rgba(56,189,248,0.15)' };
const th = { padding: '12px 14px', textAlign: 'left', color: '#cbd5e1', fontSize: 13, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 700, whiteSpace: 'nowrap' };
const td = { padding: '12px 14px', color: '#e2e8f0', fontSize: 14.5, verticalAlign: 'top' };
const label = { display: 'block', color: '#e2e8f0', fontSize: 12.5, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6, fontWeight: 600 };
const input = { width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(148,163,184,0.4)', borderRadius: 10, padding: '10px 12px', color: '#ffffff', fontSize: 15, fontFamily: 'inherit', colorScheme: 'dark' };
const solid = (bg, color = '#ffffff') => ({ background: bg, border: `1px solid ${bg}`, borderRadius: 10, padding: '10px 16px', color, fontSize: 14.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' });
const ghost = { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(148,163,184,0.4)', borderRadius: 10, padding: '10px 16px', color: '#e2e8f0', fontSize: 14.5, fontWeight: 600, cursor: 'pointer' };

const kes = (n) => `KES ${Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const day = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => new Date(Date.now() + n * 864e5).toISOString().slice(0, 10);

const METER_STATUS = {
  in_stock: ['In stock', '#10b981'], reserved: ['Reserved', '#fbbf24'], installed: ['Installed', '#38bdf8'],
  sold: ['Sold', '#a78bfa'], faulty: ['Faulty', '#f87171'], returned: ['Returned', '#cbd5e1'],
};
const INVOICE_STATUS = {
  unpaid: ['Unpaid', '#fbbf24'], part_paid: ['Part paid', '#fb923c'], paid: ['Paid', '#10b981'], void: ['Void', '#f87171'],
};
const Badge = ({ map, value }) => {
  const [text, color] = map[value] || [value, '#cbd5e1'];
  return <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 6, fontSize: 13, fontWeight: 700, color, background: `${color}22`, border: `1px solid ${color}66`, whiteSpace: 'nowrap' }}>{text}</span>;
};

function Modal({ title, onClose, children, width = 560 }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="mw-modal-overlay" onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 200, padding: '40px 20px', overflowY: 'auto' }}>
      <div className="mw-modal" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}
        style={{ background: '#0f172a', borderRadius: 16, border: '1px solid rgba(56,189,248,0.2)', padding: 28, width: '100%', maxWidth: width, boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, gap: 12 }}>
          <h3 style={{ color: '#ffffff', fontSize: 20, fontWeight: 700, margin: 0 }}>{title}</h3>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', color: '#cbd5e1', fontSize: 22, cursor: 'pointer' }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

const Field = ({ name, children, span }) => (
  <div style={{ gridColumn: span ? '1 / -1' : undefined }}>
    <label style={label}>{name}</label>
    {children}
  </div>
);
const grid2 = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 14 };

// ── Add / edit meters ──────────────────────────────
function MeterForm({ meter, headers, onDone, onClose }) {
  const editing = !!meter;
  const [f, setF] = useState(() => ({
    serials: '', serial: meter?.serial || '', model: meter?.model || '', supplier: meter?.supplier || '',
    purchase_price: meter?.purchase_price ?? '', purchase_date: meter?.purchase_date ? String(meter.purchase_date).slice(0, 10) : '',
    sale_price: meter?.sale_price ?? '', responsible_person: meter?.responsible_person || '', location: meter?.location || '',
    notes: meter?.notes || '', status: meter?.status || 'in_stock',
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const count = useMemo(() => new Set(f.serials.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean)).size, [f.serials]);

  const save = async () => {
    setBusy(true); setError('');
    try {
      const body = { ...f };
      if (editing) { delete body.serials; await axios.put(`/api/inventory/meters/${meter.id}`, body, { headers }); }
      else { delete body.serial; await axios.post('/api/inventory/meters', body, { headers }); }
      onDone();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save');
    } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove meter ${meter.serial} from inventory? Only do this for entries made by mistake.`)) return;
    try { await axios.delete(`/api/inventory/meters/${meter.id}`, { headers }); onDone(); } catch (err) { setError(err.response?.data?.message || 'Could not remove'); }
  };

  return (
    <Modal title={editing ? `Meter ${meter.serial}` : 'Add meters to stock'} onClose={onClose} width={620}>
      <div style={grid2}>
        {editing ? (
          <Field name="Serial / meter number" span><input value={f.serial} onChange={set('serial')} style={input} /></Field>
        ) : (
          <Field name={`Serial numbers — one per line${count ? ` (${count})` : ''}`} span>
            <textarea rows={4} value={f.serials} onChange={set('serials')} placeholder={'0141020001\n0141020002\n0141020003'} style={{ ...input, resize: 'vertical', fontFamily: 'ui-monospace, monospace' }} />
          </Field>
        )}
        <Field name="Model"><input value={f.model} onChange={set('model')} placeholder="e.g. Prepaid keypad DN15" style={input} /></Field>
        <Field name="Supplier"><input value={f.supplier} onChange={set('supplier')} style={input} /></Field>
        <Field name="Purchase price (KES, each)"><input type="number" min="0" inputMode="decimal" value={f.purchase_price} onChange={set('purchase_price')} style={input} /></Field>
        <Field name="Purchase date"><input type="date" value={f.purchase_date} onChange={set('purchase_date')} style={input} /></Field>
        <Field name="Sale price (KES, each)"><input type="number" min="0" inputMode="decimal" value={f.sale_price} onChange={set('sale_price')} style={input} /></Field>
        <Field name="Person responsible"><input value={f.responsible_person} onChange={set('responsible_person')} placeholder="Who holds these meters" style={input} /></Field>
        <Field name="Location"><input value={f.location} onChange={set('location')} placeholder="e.g. Kitengela store" style={input} /></Field>
        {editing && (
          <Field name="Status">
            {meter.status === 'sold' ? (
              <div style={{ color: '#e2e8f0', fontSize: 14.5, padding: '10px 0' }}>Sold on {meter.invoice_number} to {meter.sold_to}. Void that invoice to return it to stock.</div>
            ) : (
              <select value={f.status} onChange={set('status')} style={input}>
                {Object.entries(METER_STATUS).filter(([k]) => k !== 'sold').map(([k, [t]]) => <option key={k} value={k}>{t}</option>)}
              </select>
            )}
          </Field>
        )}
        <Field name="Notes" span><textarea rows={2} value={f.notes} onChange={set('notes')} style={{ ...input, resize: 'vertical' }} /></Field>
      </div>
      {error && <div style={{ color: '#f87171', fontSize: 14, marginTop: 14 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
        {editing && meter.status !== 'sold' && <button onClick={remove} style={{ ...ghost, color: '#fca5a5' }}>Remove</button>}
        <div style={{ flex: 1 }} />
        <button onClick={onClose} style={ghost}>Cancel</button>
        <button onClick={save} disabled={busy || (!editing && !count)} style={{ ...solid('#1a5fa4'), opacity: busy || (!editing && !count) ? 0.6 : 1 }}>
          {busy ? 'Saving…' : editing ? 'Save changes' : `Add ${count || ''} meter${count === 1 ? '' : 's'}`}
        </button>
      </div>
    </Modal>
  );
}

// ── New invoice ──────────────────────────────
function InvoiceForm({ headers, stock, preselected = [], onDone, onClose }) {
  const [c, setC] = useState({ customer_name: '', customer_email: '', customer_phone: '', customer_address: '', customer_kra_pin: '', issue_date: today(), due_date: plusDays(14), notes: '' });
  const [picked, setPicked] = useState(() => Object.fromEntries(preselected.map((m) => [m.id, m.sale_price ?? ''])));
  const [extra, setExtra] = useState([]);
  const [vat, setVat] = useState(false);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setC((x) => ({ ...x, [k]: e.target.value }));

  const available = stock.filter((m) => m.status === 'in_stock');
  const shown = available.filter((m) => !q || `${m.serial} ${m.model || ''} ${m.responsible_person || ''}`.toLowerCase().includes(q.toLowerCase()));
  const toggle = (m) => setPicked((p) => { const n = { ...p }; if (m.id in n) delete n[m.id]; else n[m.id] = m.sale_price ?? ''; return n; });

  const subtotal = Object.values(picked).reduce((s, v) => s + (Number(v) || 0), 0)
    + extra.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);
  const vatAmount = vat ? Math.round(subtotal * 16) / 100 : 0;

  const save = async () => {
    setBusy(true); setError('');
    try {
      const r = await axios.post('/api/inventory/invoices', {
        ...c, vat_enabled: vat,
        meters: Object.entries(picked).map(([id, price]) => ({ id: Number(id), unit_price: price })),
        items: extra.filter((l) => l.description.trim()),
      }, { headers });
      onDone(r.data.invoice);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not create the invoice');
    } finally { setBusy(false); }
  };

  return (
    <Modal title="New invoice" onClose={onClose} width={760}>
      <div style={grid2}>
        <Field name="Customer name *" span><input value={c.customer_name} onChange={set('customer_name')} placeholder="Person or company" style={input} /></Field>
        <Field name="Email"><input type="email" value={c.customer_email} onChange={set('customer_email')} style={input} /></Field>
        <Field name="Phone"><input type="tel" value={c.customer_phone} onChange={set('customer_phone')} style={input} /></Field>
        <Field name="Address"><input value={c.customer_address} onChange={set('customer_address')} style={input} /></Field>
        <Field name="Customer KRA PIN"><input value={c.customer_kra_pin} onChange={set('customer_kra_pin')} style={input} /></Field>
        <Field name="Invoice date"><input type="date" value={c.issue_date} onChange={set('issue_date')} style={input} /></Field>
        <Field name="Due date"><input type="date" value={c.due_date} onChange={set('due_date')} style={input} /></Field>
      </div>

      <div style={{ marginTop: 22 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
          <label style={{ ...label, margin: 0 }}>Meters from stock ({Object.keys(picked).length} selected)</label>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search serial or model" style={{ ...input, width: 220, padding: '7px 10px' }} />
        </div>
        <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid rgba(148,163,184,0.3)', borderRadius: 10 }}>
          {shown.length === 0 && <div style={{ color: '#cbd5e1', padding: 14, fontSize: 14.5 }}>{available.length ? 'No meters match.' : 'No meters in stock. Add some first.'}</div>}
          {shown.map((m) => {
            const on = m.id in picked;
            return (
              <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid rgba(148,163,184,0.12)', background: on ? 'rgba(74,155,224,0.12)' : 'transparent' }}>
                <input type="checkbox" checked={on} onChange={() => toggle(m)} aria-label={`Select ${m.serial}`} style={{ width: 18, height: 18 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#ffffff', fontWeight: 700, fontSize: 14.5, fontFamily: 'ui-monospace, monospace' }}>{m.serial}</div>
                  <div style={{ color: '#cbd5e1', fontSize: 13 }}>{m.model || 'No model'}{m.responsible_person ? ` · ${m.responsible_person}` : ''}</div>
                </div>
                {on && (
                  <input type="number" min="0" inputMode="decimal" value={picked[m.id]} aria-label={`Price for ${m.serial}`}
                    onChange={(e) => setPicked((p) => ({ ...p, [m.id]: e.target.value }))} style={{ ...input, width: 120, padding: '7px 10px' }} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ marginTop: 18 }}>
        <label style={label}>Other lines (installation, transport…)</label>
        {extra.map((l, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 70px 110px 36px', gap: 8, marginBottom: 8 }}>
            <input value={l.description} placeholder="Description" onChange={(e) => setExtra((x) => x.map((y, j) => (j === i ? { ...y, description: e.target.value } : y)))} style={input} />
            <input type="number" min="0" value={l.quantity} aria-label="Quantity" onChange={(e) => setExtra((x) => x.map((y, j) => (j === i ? { ...y, quantity: e.target.value } : y)))} style={input} />
            <input type="number" min="0" value={l.unit_price} placeholder="Price" aria-label="Unit price" onChange={(e) => setExtra((x) => x.map((y, j) => (j === i ? { ...y, unit_price: e.target.value } : y)))} style={input} />
            <button onClick={() => setExtra((x) => x.filter((_, j) => j !== i))} aria-label="Remove line" style={{ ...ghost, padding: 0 }}>✕</button>
          </div>
        ))}
        <button onClick={() => setExtra((x) => [...x, { description: '', quantity: 1, unit_price: '' }])} style={{ ...ghost, padding: '8px 14px' }}>+ Add line</button>
      </div>

      <div style={{ marginTop: 18 }}>
        <Field name="Notes on the invoice"><textarea rows={2} value={c.notes} onChange={set('notes')} style={{ ...input, resize: 'vertical' }} /></Field>
      </div>

      <div style={{ marginTop: 18, padding: 14, borderRadius: 12, background: 'rgba(255,255,255,0.04)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#ffffff', fontSize: 15, cursor: 'pointer' }}>
          <input type="checkbox" checked={vat} onChange={(e) => setVat(e.target.checked)} style={{ width: 18, height: 18 }} /> Add VAT (16%)
        </label>
        <div style={{ textAlign: 'right', color: '#e2e8f0', fontSize: 14.5 }}>
          <div>Subtotal {kes(subtotal)}{vat ? ` · VAT ${kes(vatAmount)}` : ''}</div>
          <div style={{ color: '#ffffff', fontSize: 20, fontWeight: 800 }}>Total {kes(subtotal + vatAmount)}</div>
        </div>
      </div>

      {error && <div style={{ color: '#f87171', fontSize: 14, marginTop: 14 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <button onClick={onClose} style={ghost}>Cancel</button>
        <button onClick={save} disabled={busy || !c.customer_name.trim()} style={{ ...solid('#1a5fa4'), opacity: busy || !c.customer_name.trim() ? 0.6 : 1 }}>
          {busy ? 'Creating…' : 'Create invoice'}
        </button>
      </div>
    </Modal>
  );
}

// ── Invoice detail: payments, PDF, email, void ──────────────────────────────
function InvoiceDetail({ id, headers, onChanged, onClose }) {
  const [data, setData] = useState(null);
  const [pay, setPay] = useState({ amount: '', paid_on: today(), method: 'M-Pesa', reference: '' });
  const [mail, setMail] = useState({ to: '', message: '' });
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);

  const load = useCallback(() => axios.get(`/api/inventory/invoices/${id}`, { headers }).then((r) => {
    setData(r.data);
    const bal = Number(r.data.invoice.total) - Number(r.data.invoice.amount_paid);
    setPay((p) => ({ ...p, amount: bal > 0 ? bal.toFixed(2) : '' }));
    setMail((m) => ({ ...m, to: m.to || r.data.invoice.customer_email || '' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (name, fn, okText) => {
    setBusy(name); setMsg(null);
    try { const r = await fn(); if (r?.data?.invoice) setData(r.data); setMsg({ ok: true, text: okText }); onChanged(); }
    catch (err) { setMsg({ ok: false, text: err.response?.data?.message || 'Something went wrong' }); }
    finally { setBusy(''); }
  };

  const openPdf = async (download) => {
    const w = download ? null : window.open('', '_blank');
    try {
      const r = await axios.get(`/api/inventory/invoices/${id}/pdf`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      if (download) {
        const a = document.createElement('a'); a.href = url; a.download = `${data.invoice.number}.pdf`; a.click();
      } else if (w) { w.location.href = url; } else { window.location.href = url; }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { if (w) w.close(); setMsg({ ok: false, text: 'Could not create the PDF' }); }
  };

  if (!data) return <Modal title="Invoice" onClose={onClose}><p style={{ color: '#cbd5e1' }}>Loading…</p></Modal>;
  const { invoice: inv, items, payments } = data;
  const balance = Math.max(0, Number(inv.total) - Number(inv.amount_paid));
  const open = inv.status !== 'void';

  return (
    <Modal title={`Invoice ${inv.number}`} onClose={onClose} width={760}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <div style={{ color: '#ffffff', fontSize: 17, fontWeight: 700 }}>{inv.customer_name}</div>
          <div style={{ color: '#cbd5e1', fontSize: 14 }}>{[inv.customer_email, inv.customer_phone, inv.customer_kra_pin && `KRA ${inv.customer_kra_pin}`].filter(Boolean).join(' · ')}</div>
          <div style={{ color: '#cbd5e1', fontSize: 14, marginTop: 4 }}>Issued {day(inv.issue_date)} · Due {day(inv.due_date)}{inv.emailed_at ? ` · Emailed ${day(inv.emailed_at)}` : ''}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <Badge map={INVOICE_STATUS} value={inv.status} />
          <div style={{ color: '#ffffff', fontSize: 22, fontWeight: 800, marginTop: 6 }}>{kes(inv.total)}</div>
          {open && <div style={{ color: balance ? '#fbbf24' : '#10b981', fontSize: 14, fontWeight: 600 }}>{balance ? `Balance ${kes(balance)}` : 'Fully paid'}</div>}
        </div>
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>Item</th><th style={{ ...th, textAlign: 'right' }}>Qty</th><th style={{ ...th, textAlign: 'right' }}>Price</th><th style={{ ...th, textAlign: 'right' }}>Amount</th></tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} style={{ borderTop: '1px solid rgba(148,163,184,0.12)' }}>
                <td style={td}>{it.description}</td>
                <td style={{ ...td, textAlign: 'right' }}>{Number(it.quantity)}</td>
                <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{kes(it.unit_price)}</td>
                <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{kes(it.line_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ textAlign: 'right', color: '#e2e8f0', fontSize: 14.5, marginTop: 8 }}>
        Subtotal {kes(inv.subtotal)}{inv.vat_enabled ? ` · VAT (${Number(inv.vat_rate)}%) ${kes(inv.vat_amount)}` : ''}
      </div>

      {/* Payments */}
      <div style={{ marginTop: 20 }}>
        <label style={label}>Payments received</label>
        {payments.length === 0 && <div style={{ color: '#cbd5e1', fontSize: 14.5 }}>None yet.</div>}
        {payments.map((p) => (
          <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid rgba(148,163,184,0.12)', color: '#e2e8f0', fontSize: 14.5 }}>
            <span>{day(p.paid_on)} · <b style={{ color: '#ffffff' }}>{kes(p.amount)}</b>{p.method ? ` · ${p.method}` : ''}{p.reference ? ` · Ref ${p.reference}` : ''}</span>
            <button onClick={() => window.confirm('Remove this payment?') && act('rmpay', () => axios.delete(`/api/inventory/invoices/${inv.id}/payments/${p.id}`, { headers }), 'Payment removed')}
              style={{ background: 'none', border: 'none', color: '#fca5a5', cursor: 'pointer', fontSize: 14 }}>Remove</button>
          </div>
        ))}
        {open && balance > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 130px), 1fr))', gap: 8, marginTop: 10, alignItems: 'end' }}>
            <div><label style={label}>Amount</label><input type="number" min="0" inputMode="decimal" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} style={input} /></div>
            <div><label style={label}>Date</label><input type="date" value={pay.paid_on} onChange={(e) => setPay({ ...pay, paid_on: e.target.value })} style={input} /></div>
            <div><label style={label}>Method</label>
              <select value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} style={input}>
                {['M-Pesa', 'Bank transfer', 'Cash', 'Cheque', 'Other'].map((m) => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div><label style={label}>Reference</label><input value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} placeholder="e.g. RKX12…" style={input} /></div>
            <button disabled={busy === 'pay' || !Number(pay.amount)} onClick={() => act('pay', () => axios.post(`/api/inventory/invoices/${inv.id}/payments`, pay, { headers }), 'Payment recorded')}
              style={{ ...solid('#047857'), opacity: busy === 'pay' || !Number(pay.amount) ? 0.6 : 1 }}>{busy === 'pay' ? 'Saving…' : 'Record payment'}</button>
          </div>
        )}
      </div>

      {/* Email */}
      {open && (
        <div style={{ marginTop: 20 }}>
          <label style={label}>Email the invoice (PDF attached)</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 8 }}>
            <input type="email" value={mail.to} onChange={(e) => setMail({ ...mail, to: e.target.value })} placeholder="customer@example.com" style={input} />
            <button disabled={busy === 'mail' || !mail.to} onClick={() => act('mail', () => axios.post(`/api/inventory/invoices/${inv.id}/email`, mail, { headers }).then(load), `Sent to ${mail.to}`)}
              style={{ ...solid('#1a5fa4'), opacity: busy === 'mail' || !mail.to ? 0.6 : 1 }}>{busy === 'mail' ? 'Sending…' : 'Send'}</button>
          </div>
          <textarea rows={2} value={mail.message} onChange={(e) => setMail({ ...mail, message: e.target.value })} placeholder="Optional message to include" style={{ ...input, marginTop: 8, resize: 'vertical' }} />
        </div>
      )}

      {msg && <div role="status" style={{ marginTop: 14, color: msg.ok ? '#34d399' : '#f87171', fontSize: 14.5, fontWeight: 600 }}>{msg.text}</div>}

      <div style={{ display: 'flex', gap: 10, marginTop: 22, flexWrap: 'wrap' }}>
        {open && Number(inv.amount_paid) === 0 && (
          <button onClick={() => window.confirm(`Void ${inv.number}? Its meters go back into stock.`) && act('void', () => axios.post(`/api/inventory/invoices/${inv.id}/void`, {}, { headers }), 'Invoice voided — meters are back in stock')}
            style={{ ...ghost, color: '#fca5a5' }}>Void invoice</button>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={() => openPdf(true)} style={ghost}>Download PDF</button>
        <button onClick={() => openPdf(false)} style={solid('#1a5fa4')}>Open / print PDF</button>
      </div>
    </Modal>
  );
}

// ── Invoice header settings ──────────────────────────────
function SettingsTab({ headers }) {
  const [s, setS] = useState(null);
  const [msg, setMsg] = useState(null);
  useEffect(() => { axios.get('/api/inventory/settings', { headers }).then((r) => setS(r.data.settings)).catch(() => setS({})); }, [headers]);
  if (!s) return null;
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const save = async () => {
    setMsg(null);
    try { const r = await axios.put('/api/inventory/settings', s, { headers }); setS(r.data.settings); setMsg({ ok: true, text: 'Saved — new PDFs use these details' }); }
    catch (err) { setMsg({ ok: false, text: err.response?.data?.message || 'Could not save' }); }
  };
  return (
    <div style={{ ...panel, padding: 24, maxWidth: 760 }}>
      <h3 style={{ color: '#ffffff', fontSize: 19, fontWeight: 700, margin: '0 0 6px' }}>Details printed on invoices</h3>
      <p style={{ color: '#cbd5e1', fontSize: 14.5, margin: '0 0 18px' }}>Shown at the top of every invoice PDF and in invoice emails.</p>
      <div style={grid2}>
        <Field name="Company name"><input value={s.invoice_company_name} onChange={set('invoice_company_name')} placeholder="Majic Water Systems Ltd" style={input} /></Field>
        <Field name="KRA PIN"><input value={s.invoice_kra_pin} onChange={set('invoice_kra_pin')} style={input} /></Field>
        <Field name="Phone"><input value={s.invoice_phone} onChange={set('invoice_phone')} style={input} /></Field>
        <Field name="Email"><input type="email" value={s.invoice_email} onChange={set('invoice_email')} style={input} /></Field>
        <Field name="Address" span><textarea rows={2} value={s.invoice_address} onChange={set('invoice_address')} style={{ ...input, resize: 'vertical' }} /></Field>
        <Field name="How to pay (paybill, bank details)" span><textarea rows={3} value={s.invoice_payment_details} onChange={set('invoice_payment_details')} placeholder={'M-Pesa Paybill 123456, Account: invoice number\nBank: …'} style={{ ...input, resize: 'vertical' }} /></Field>
      </div>
      {msg && <div style={{ marginTop: 12, color: msg.ok ? '#34d399' : '#f87171', fontWeight: 600 }}>{msg.text}</div>}
      <button onClick={save} style={{ ...solid('#1a5fa4'), marginTop: 18 }}>Save details</button>
    </div>
  );
}

// ── Page ──────────────────────────────
export default function InventoryPage() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const headers = useMemo(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);
  const [tab, setTab] = useState('meters');
  const [summary, setSummary] = useState(null);
  const [meters, setMeters] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [mFilter, setMFilter] = useState({ status: '', q: '' });
  const [iFilter, setIFilter] = useState({ status: '', q: '' });
  const [selected, setSelected] = useState({});
  const [modal, setModal] = useState(null); // {type:'add'|'edit'|'invoice'|'detail', ...}
  const [error, setError] = useState('');

  const fail = (err) => {
    if (err.response?.status === 401) { localStorage.clear(); navigate('/login'); }
    else if (err.response?.status === 403) setError('Inventory is for admins only.');
    else setError('Could not load inventory');
  };
  const loadSummary = useCallback(() => axios.get('/api/inventory/summary', { headers }).then((r) => setSummary(r.data)).catch(fail),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [headers]);
  const loadMeters = useCallback(() => axios.get('/api/inventory/meters', { headers, params: { status: mFilter.status || undefined, q: mFilter.q || undefined } })
    .then((r) => setMeters(r.data.meters)).catch(fail),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [headers, mFilter]);
  const loadInvoices = useCallback(() => axios.get('/api/inventory/invoices', { headers, params: { status: iFilter.status || undefined, q: iFilter.q || undefined } })
    .then((r) => setInvoices(r.data.invoices)).catch(fail),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [headers, iFilter]);
  const [stock, setStock] = useState([]);
  const loadStock = useCallback(() => axios.get('/api/inventory/meters', { headers, params: { status: 'in_stock' } }).then((r) => setStock(r.data.meters)).catch(() => {}), [headers]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { const t = setTimeout(loadMeters, 250); return () => clearTimeout(t); }, [loadMeters]);
  useEffect(() => { const t = setTimeout(loadInvoices, 250); return () => clearTimeout(t); }, [loadInvoices]);
  const refresh = () => { loadSummary(); loadMeters(); loadInvoices(); loadStock(); };

  if (user.role !== 'admin') {
    return (<div style={{ minHeight: '100vh' }}><Navbar user={user} /><div className="mw-page" style={{ padding: '32px 24px', maxWidth: 1200, margin: '0 auto' }}><h1 style={{ color: '#0f172a' }}>Inventory</h1><p style={{ color: '#475569' }}>This page is for admins only.</p></div></div>);
  }

  const selectedMeters = meters.filter((m) => selected[m.id] && m.status === 'in_stock');
  const startInvoice = async (pre = []) => { await loadStock(); setModal({ type: 'invoice', pre }); };
  const card = (title, value, sub, color) => (
    <div style={{ ...panel, padding: 20 }}>
      <div style={{ fontSize: 13, color: '#e2e8f0', letterSpacing: 1.2, textTransform: 'uppercase', fontWeight: 700, marginBottom: 10 }}>{title}</div>
      <div style={{ fontSize: 30, fontWeight: 800, color, lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 14, color: '#cbd5e1', marginTop: 6 }}>{sub}</div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh' }}>
      <Navbar user={user} />
      <div className="mw-page" style={{ padding: '32px 24px', maxWidth: 1200, margin: '0 auto' }}>
        <div className="mw-page-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, gap: 12 }}>
          <div>
            <h1 style={{ color: '#0f172a', fontSize: 26, fontWeight: 700, margin: 0 }}>Inventory</h1>
            <p style={{ color: '#475569', fontSize: 14, marginTop: 6 }}>Meters in stock, who holds them, what they cost, and invoices for meters sold</p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setModal({ type: 'add' })} style={solid('#047857')}>+ Add meters</button>
            <button onClick={() => startInvoice()} style={solid('#1a5fa4')}>+ New invoice</button>
          </div>
        </div>

        {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 16px', color: '#b91c1c', marginBottom: 20 }}>{error}</div>}

        {summary && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 230px), 1fr))', gap: 16, marginBottom: 24 }}>
            {card('In stock', summary.meters.in_stock, `${kes(summary.meters.stock_cost)} at cost · ${kes(summary.meters.stock_sale_value)} to sell`, '#38bdf8')}
            {card('Sold', summary.meters.sold, `${kes(summary.sales.meter_revenue)} in meter sales`, '#a78bfa')}
            {card('Profit on meters sold', kes(summary.sales.gross_profit), `Sales minus purchase cost (${kes(summary.sales.sold_cost)})`, '#34d399')}
            {card('Owed on invoices', kes(summary.invoices.outstanding), `${kes(summary.invoices.collected)} collected so far`, '#fbbf24')}
          </div>
        )}

        <div role="tablist" style={{ display: 'flex', gap: 4, marginBottom: 20, background: '#0f172a', borderRadius: 12, padding: 4, width: 'fit-content', maxWidth: '100%', flexWrap: 'wrap' }}>
          {[['meters', 'Meters'], ['invoices', 'Invoices'], ['settings', 'Invoice details']].map(([id, t]) => (
            <button key={id} role="tab" aria-selected={tab === id} onClick={() => setTab(id)}
              style={{ background: tab === id ? 'rgba(74,155,224,0.25)' : 'none', border: 'none', borderRadius: 8, padding: '9px 16px', color: tab === id ? '#7cc4ff' : '#e2e8f0', fontSize: 14.5, fontWeight: tab === id ? 700 : 500, cursor: 'pointer' }}>{t}</button>
          ))}
        </div>

        {tab === 'meters' && (
          <div style={{ ...panel, overflow: 'hidden' }}>
            <div style={{ display: 'flex', gap: 10, padding: 16, flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid rgba(56,189,248,0.1)' }}>
              <input value={mFilter.q} onChange={(e) => setMFilter({ ...mFilter, q: e.target.value })} placeholder="Search serial, model, person, customer" style={{ ...input, flex: '1 1 220px', width: 'auto' }} />
              <select value={mFilter.status} onChange={(e) => setMFilter({ ...mFilter, status: e.target.value })} style={{ ...input, flex: '0 1 170px', width: 'auto' }}>
                <option value="">All statuses</option>
                {Object.entries(METER_STATUS).map(([k, [t]]) => <option key={k} value={k}>{t}</option>)}
              </select>
              {selectedMeters.length > 0 && (
                <button onClick={() => startInvoice(selectedMeters)} style={solid('#1a5fa4')}>Invoice {selectedMeters.length} selected</button>
              )}
            </div>
            {meters.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#cbd5e1', fontSize: 15 }}>
                {mFilter.q || mFilter.status ? 'No meters match these filters.' : 'No meters in inventory yet.'}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ borderBottom: '1px solid rgba(56,189,248,0.1)' }}>
                    <th style={{ ...th, width: 36 }} aria-label="Select" />
                    {['Serial', 'Model', 'Status', 'Responsible', 'Location', 'Bought for', 'Sells for', 'Sold to'].map((h) => <th key={h} style={th}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {meters.map((m) => (
                      <tr key={m.id} onClick={() => setModal({ type: 'edit', meter: m })} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer' }}>
                        <td style={td} onClick={(e) => e.stopPropagation()}>
                          {m.status === 'in_stock' && <input type="checkbox" aria-label={`Select ${m.serial}`} checked={!!selected[m.id]} onChange={(e) => setSelected({ ...selected, [m.id]: e.target.checked })} style={{ width: 18, height: 18 }} />}
                        </td>
                        <td style={{ ...td, color: '#7cc4ff', fontWeight: 700, fontFamily: 'ui-monospace, monospace', whiteSpace: 'nowrap' }}>{m.serial}</td>
                        <td style={td}>{m.model || '—'}</td>
                        <td style={td}><Badge map={METER_STATUS} value={m.status} /></td>
                        <td style={td}>{m.responsible_person || <span style={{ color: '#fbbf24' }}>Not set</span>}</td>
                        <td style={td}>{m.location || '—'}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{m.purchase_price != null ? kes(m.purchase_price) : '—'}{m.purchase_date ? <div style={{ color: '#cbd5e1', fontSize: 13 }}>{day(m.purchase_date)}</div> : null}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap', color: '#34d399', fontWeight: 600 }}>{m.sale_price != null ? kes(m.sale_price) : '—'}</td>
                        <td style={td}>{m.sold_to ? <>{m.sold_to}<div style={{ color: '#cbd5e1', fontSize: 13 }}>{m.invoice_number}</div></> : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === 'invoices' && (
          <div style={{ ...panel, overflow: 'hidden' }}>
            <div style={{ display: 'flex', gap: 10, padding: 16, flexWrap: 'wrap', borderBottom: '1px solid rgba(56,189,248,0.1)' }}>
              <input value={iFilter.q} onChange={(e) => setIFilter({ ...iFilter, q: e.target.value })} placeholder="Search invoice number or customer" style={{ ...input, flex: '1 1 220px', width: 'auto' }} />
              <select value={iFilter.status} onChange={(e) => setIFilter({ ...iFilter, status: e.target.value })} style={{ ...input, flex: '0 1 170px', width: 'auto' }}>
                <option value="">All invoices</option>
                <option value="open">Not fully paid</option>
                {Object.entries(INVOICE_STATUS).map(([k, [t]]) => <option key={k} value={k}>{t}</option>)}
              </select>
            </div>
            {invoices.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#cbd5e1', fontSize: 15 }}>No invoices yet.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ borderBottom: '1px solid rgba(56,189,248,0.1)' }}>
                    {['Invoice', 'Customer', 'Date', 'Due', 'Meters', 'Total', 'Balance', 'Status'].map((h) => <th key={h} style={th}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {invoices.map((i) => {
                      const bal = i.status === 'void' ? 0 : Number(i.total) - Number(i.amount_paid);
                      const overdue = bal > 0 && i.due_date && new Date(i.due_date) < new Date(today());
                      return (
                        <tr key={i.id} onClick={() => setModal({ type: 'detail', id: i.id })} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer' }}>
                          <td style={{ ...td, color: '#7cc4ff', fontWeight: 700, whiteSpace: 'nowrap' }}>{i.number}</td>
                          <td style={td}>{i.customer_name}</td>
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>{day(i.issue_date)}</td>
                          <td style={{ ...td, whiteSpace: 'nowrap', color: overdue ? '#f87171' : td.color, fontWeight: overdue ? 700 : 400 }}>{day(i.due_date)}{overdue ? ' · overdue' : ''}</td>
                          <td style={td}>{i.meter_count}</td>
                          <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 600 }}>{kes(i.total)}</td>
                          <td style={{ ...td, whiteSpace: 'nowrap', color: bal > 0 ? '#fbbf24' : '#cbd5e1' }}>{kes(bal)}</td>
                          <td style={td}><Badge map={INVOICE_STATUS} value={i.status} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === 'settings' && <SettingsTab headers={headers} />}
      </div>

      {modal?.type === 'add' && <MeterForm headers={headers} onClose={() => setModal(null)} onDone={() => { setModal(null); refresh(); }} />}
      {modal?.type === 'edit' && <MeterForm meter={modal.meter} headers={headers} onClose={() => setModal(null)} onDone={() => { setModal(null); refresh(); }} />}
      {modal?.type === 'invoice' && (
        <InvoiceForm headers={headers} stock={stock} preselected={modal.pre} onClose={() => setModal(null)}
          onDone={(inv) => { setSelected({}); refresh(); setTab('invoices'); setModal({ type: 'detail', id: inv.id }); }} />
      )}
      {modal?.type === 'detail' && <InvoiceDetail id={modal.id} headers={headers} onChanged={refresh} onClose={() => setModal(null)} />}
    </div>
  );
}

